import { useState } from 'react'
import { Apple, Download, Monitor, MoreVertical, Share, SquarePlus } from 'lucide-react'
import { useInstalacion } from '../store/instalacion'
import { Boton } from './ui/base'
import { Hoja } from './ui/Hoja'

// Un iPad con "Solicitar sitio de escritorio" (el default desde iPadOS
// 13) se anuncia como un Mac cualquiera: mismo user agent, sin "iPad" en
// ningún lado. La única forma de distinguirlo de un Mac de verdad es que
// tiene pantalla táctil —`maxTouchPoints`— y un Mac no.
const esIOS = () =>
  typeof navigator !== 'undefined' &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1))

const esMacOS = () =>
  typeof navigator !== 'undefined' && /macintosh|mac os x/i.test(navigator.userAgent) && !esIOS()

const esAndroid = () => typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent)

/** Botón chico y bien visible ("App", rojo neón) que abre las
 *  instrucciones para instalar NovaBerry en el teléfono: en Android
 *  usa el prompt nativo del navegador si está disponible, y en iPhone
 *  (donde Safari no lo ofrece) muestra el paso a paso de "Compartir →
 *  Añadir a pantalla de inicio". */
export function BotonInstalarApp() {
  const { evento, instalada, limpiar } = useInstalacion()
  const [abierto, setAbierto] = useState(false)
  const [instalando, setInstalando] = useState(false)

  if (instalada) return null

  const instalarAhora = async () => {
    if (!evento) return
    setInstalando(true)
    try {
      await evento.prompt()
      const { outcome } = await evento.userChoice
      if (outcome === 'accepted') limpiar()
    } finally {
      setInstalando(false)
    }
  }

  // Se calcula una sola vez por apertura, no en cada tarjeta: el user
  // agent no cambia a mitad de render y así las tres son mutuamente
  // excluyentes de verdad (nunca se ve la de Android en un Mac).
  const ios = esIOS()
  const mac = esMacOS()
  const android = esAndroid()
  const escritorio = !ios && !android // Mac, Windows o Linux

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border-2 px-3.5 py-1.5 text-[12px] font-bold tracking-wide uppercase transition-transform active:scale-95"
        style={{
          borderColor: '#ff1a3c',
          color: '#ff1a3c',
          boxShadow: '0 0 0 1px rgba(255,26,60,0.25), 0 0 14px 1px rgba(255,26,60,0.55)',
        }}
      >
        <Download className="size-3.5" strokeWidth={2.6} />
        App
      </button>

      <Hoja
        abierta={abierto}
        onCerrar={() => setAbierto(false)}
        titulo="Instalar NovaBerry"
        descripcion="Se abre como una app de verdad, con ícono propio y sin la barra del navegador."
        ancho="sm"
      >
        <div className="space-y-4 pb-2">
          {evento && (
            <Boton
              bloque
              tamano="lg"
              icono={<Download className="size-5" />}
              cargando={instalando}
              onClick={instalarAhora}
            >
              Instalar ahora
            </Boton>
          )}

          {/* Mac, Windows o Linux con Chrome/Edge: el botón de arriba ya
           *  instala solo. Esta tarjeta aparece nada más mientras ese
           *  botón no está disponible —el navegador todavía no decidió
           *  que la app es instalable, o es Firefox, que no lo ofrece. */}
          {escritorio && !evento && (
            <div className="rounded-2xl superficie-hundida p-4">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-ambar-50 text-ambar-600 dark:bg-ambar-500/15 dark:text-ambar-300">
                  <Monitor className="size-4" />
                </span>
                <h3 className="text-[14px] font-bold texto-base">Chrome o Edge</h3>
              </div>
              <p className="text-[13px] texto-suave">
                Busca el ícono de instalar <Download className="inline size-3.5 -mt-0.5" /> al
                lado derecho de la barra de direcciones, o abre el menú (⋮) y elige «Instalar
                NovaBerry».
              </p>
            </div>
          )}

          {/* Mac con Safari: Apple no deja que ninguna página dispare la
           *  instalación por código —tiene que ser la persona, desde el
           *  menú del navegador. Por eso esta tarjeta no depende de
           *  `evento`: en Safari nunca llega, aunque el resto de los
           *  requisitos esté cumplido —es la única forma en ese
           *  navegador, con o sin el botón de arriba disponible. */}
          {mac && (
            <div className="rounded-2xl superficie-hundida p-4">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-tinta-50 text-tinta-600 dark:bg-tinta-500/15 dark:text-tinta-200">
                  <Apple className="size-4" />
                </span>
                <h3 className="text-[14px] font-bold texto-base">¿Usas Safari?</h3>
              </div>
              <ol className="list-decimal space-y-1.5 pl-4 text-[13px] texto-suave">
                <li>
                  Abre <strong className="texto-base">app.novaberry.cl</strong> en Safari.
                </li>
                <li>
                  En la barra de menú, ve a <strong className="texto-base">Archivo</strong> →{' '}
                  <strong className="texto-base">Añadir al Dock…</strong> (en versiones más
                  viejas de macOS: el ícono de compartir{' '}
                  <Share className="inline size-3.5 -mt-0.5" /> en la barra de direcciones).
                </li>
                <li>Confirma. Queda un ícono en el Dock, como cualquier app.</li>
              </ol>
            </div>
          )}

          {/* Android */}
          {android && (
            <div className="rounded-2xl superficie-hundida p-4">
              <div className="mb-2.5 flex items-center gap-2">
                <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-lima-50 text-lima-700 dark:bg-lima-500/15 dark:text-lima-300">
                  <MoreVertical className="size-4" />
                </span>
                <h3 className="text-[14px] font-bold texto-base">Android (Chrome)</h3>
              </div>
              {evento ? (
                <p className="text-[13px] texto-suave">
                  Toca «Instalar ahora» arriba —o el aviso que te muestre el navegador.
                </p>
              ) : (
                <ol className="list-decimal space-y-1 pl-4 text-[13px] texto-suave">
                  <li>Abre el menú del navegador (⋮, arriba a la derecha).</li>
                  <li>Elige «Instalar app» o «Añadir a pantalla de inicio».</li>
                  <li>Confirma. Queda un ícono en tu teléfono, como cualquier app.</li>
                </ol>
              )}
            </div>
          )}

          {/* iPhone / iPad: mismo caso que Mac Safari —Apple exige que
           *  sea la persona quien lo haga a mano desde Compartir, nunca
           *  un botón de la página. Se muestra siempre, no solo cuando
           *  se detecta iOS: sirve como referencia para pasarle los
           *  pasos a alguien que va a instalarla en su teléfono. */}
          <div className="rounded-2xl superficie-hundida p-4">
            <div className="mb-2.5 flex items-center gap-2">
              <span className="grid size-8 shrink-0 place-items-center rounded-xl bg-tinta-50 text-tinta-600 dark:bg-tinta-500/15 dark:text-tinta-200">
                <Apple className="size-4" />
              </span>
              <h3 className="text-[14px] font-bold texto-base">iPhone / iPad (Safari)</h3>
            </div>
            <ol className="list-decimal space-y-1.5 pl-4 text-[13px] texto-suave">
              <li>
                Abre <strong className="texto-base">app.novaberry.cl</strong> en{' '}
                <strong className="texto-base">Safari</strong> (no funciona desde Chrome en
                iPhone —Apple no se lo permite a ningún navegador salvo Safari).
              </li>
              <li>
                Toca el ícono de compartir <Share className="inline size-3.5 -mt-0.5" /> en la
                barra de abajo (o de arriba, en iPad).
              </li>
              <li>
                Baja y elige <strong className="texto-base">«Añadir a pantalla de inicio»</strong>{' '}
                <SquarePlus className="inline size-3.5 -mt-0.5" />.
              </li>
              <li>Toca «Añadir». Queda un ícono en tu pantalla, como cualquier app.</li>
            </ol>
          </div>

          {(ios || mac || android) && (
            <p className="text-center text-[12px] texto-tenue">
              Detectamos que estás en {ios ? 'un iPhone/iPad' : mac ? 'un Mac' : 'Android'} —sigue
              esos pasos.
            </p>
          )}
        </div>
      </Hoja>
    </>
  )
}
